import { useState, useEffect, lazy, Suspense } from 'react';
import { Tabs, Tab, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import { TabPanel, BrowserTitle, Spinner } from '../components';

const EquipmentTypesTable = lazy(() => import('../components/Tables/Items/items.equipmentTypes'));
const EquipmentMakesTable = lazy(() => import('../components/Tables/Items/items.equipmentMakes'));
const EquipmentModelsTable = lazy(() => import('../components/Tables/Items/items.equipmentModels'));
const ScopeOfWorkTable = lazy(() => import('../components/Tables/Items/items.works'));
const WorkDescriptionTable = lazy(() => import('../components/Tables/Items/items.workDescription'));
const WarrantyTable = lazy(() => import('../components/Tables/Items/items.warranty'));
const GuaranteesTable = lazy(() => import('../components/Tables/Items/items.guarantees'));
const RebatesTable = lazy(() => import('../components/Tables/Items/items.rebates'));
const PaymentMethodsTable = lazy(() => import('../components/Tables/Items/items.paymentMethods'));
const AirflowItemsTable = lazy(() => import('../components/Tables/Items/items.airflow'));
const DiscountsTable = lazy(() => import('../components/Tables/Items/items.discounts'));
const RegistrationTable = lazy(() => import('../components/Tables/Items/items.registration'));

function addTabIndex(index) {
  return {
    id: `items-tab-${index}`,
    'aria-controls': `items-tabpanel-${index}`,
  };
}

const tabStyle = { color: '#0D3B59 !important', fontFamily: 'Lato, sans-serif' };

export const Items = () => {
  const location = window.location.pathname;
  const history = useNavigate();
  const [activeTab, setActiveTab] = useState(0);

  const tabSwitcher = (e, val) => {
    setActiveTab(val);

    switch (val) {
      case 1:
        history('/items/types');
        break;
      case 2:
        history('/items/makes');
        break;
      case 3:
        history('/items/scope-of-work');
        break;
      case 4:
        history('/items/work-description');
        break;
      case 5:
        history('/items/warranty');
        break;
      case 6:
        history('/items/guarantees');
        break;
      case 7:
        history('/items/rebates');
        break;
      case 8:
        history('/items/payments');
        break;
      case 9:
        history('/items/airflow');
        break;
      case 10:
        history('/items/discounts');
        break;
      case 11:
        history('/items/registration');
        break;
      default:
        history('/items');
        break;
    }
  };

  useEffect(() => {
    switch (location) {
      case '/items/types':
        setActiveTab(1);
        break;
      case '/items/makes':
        setActiveTab(2);
        break;
      case '/items/scope-of-work':
        setActiveTab(3);
        break;
      case '/items/work-description':
        setActiveTab(4);
        break;
      case '/items/warranty':
        setActiveTab(5);
        break;
      case '/items/guarantees':
        setActiveTab(6);
        break;
      case '/items/rebates':
        setActiveTab(7);
        break;
      case '/items/payments':
        setActiveTab(8);
        break;
      case '/items/airflow':
        setActiveTab(9);
        break;
      case '/items/discounts':
        setActiveTab(10);
        break;
      case '/items/registration':
        setActiveTab(11);
        break;
      default:
        setActiveTab(0);
        break;
    }
  }, [location]);

  return (
    <Box component="section" id="items-page">
      <Box sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={tabSwitcher}
            aria-label="items tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Equipment (Models)" {...addTabIndex(0)} sx={tabStyle} />
            <Tab label="Types" {...addTabIndex(1)} sx={tabStyle} />
            <Tab label="Manufacturers" {...addTabIndex(2)} sx={tabStyle} />
            <Tab label="Scope of Work" {...addTabIndex(3)} sx={tabStyle} />
            <Tab label="Work Description" {...addTabIndex(4)} sx={tabStyle} />
            <Tab label="Warranty" {...addTabIndex(5)} sx={tabStyle} />
            <Tab label="Guarantees" {...addTabIndex(6)} sx={tabStyle} />
            <Tab label="Rebates" {...addTabIndex(7)} sx={tabStyle} />
            <Tab label="Payments" {...addTabIndex(8)} sx={tabStyle} />
            <Tab label="Airflow" {...addTabIndex(9)} sx={tabStyle} />
            <Tab label="Discounts" {...addTabIndex(10)} sx={tabStyle} />
            <Tab label="Registration" {...addTabIndex(11)} sx={tabStyle} />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          {activeTab === 0 && <BrowserTitle title="Equipment Models | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <EquipmentModelsTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {activeTab === 1 && <BrowserTitle title="Equipment Types | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <EquipmentTypesTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {activeTab === 2 && <BrowserTitle title="Manufacturers | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <EquipmentMakesTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={3}>
          {activeTab === 3 && <BrowserTitle title="Scope of Work | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <ScopeOfWorkTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={4}>
          {activeTab === 4 && <BrowserTitle title="Work Description | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <WorkDescriptionTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={5}>
          {activeTab === 5 && <BrowserTitle title="Warranty | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <WarrantyTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={6}>
          {activeTab === 6 && <BrowserTitle title="Guarantees | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <GuaranteesTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={7}>
          {activeTab === 7 && <BrowserTitle title="Rebates | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <RebatesTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={8}>
          {activeTab === 8 && <BrowserTitle title="Payment Methods | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <PaymentMethodsTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={9}>
          {activeTab === 9 && <BrowserTitle title="Airflow Items | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <AirflowItemsTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={10}>
          {activeTab === 10 && <BrowserTitle title="Discounts Catalog | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <DiscountsTable />
          </Suspense>
        </TabPanel>

        <TabPanel value={activeTab} index={11}>
          {activeTab === 11 && <BrowserTitle title="Equipment Registration | Airmaxx Pro" />}
          <Suspense fallback={<Spinner />}>
            <RegistrationTable />
          </Suspense>
        </TabPanel>
      </Box>
    </Box>
  );
};
